import {autoRegister, resolve} from "../../container";
import {promiseFromJsonResponse} from "../../common/utils/promises";
import {SecuredRequestExecutor} from "../../module/awswaf/securedRequestExecutor";
import {LanguagesService} from "../../common/languages";

export class JobPreviewRequest {
    public constructor(
        public readonly jobTypes: string[],
        public readonly jobLocations: string[],
        public readonly jobCompanies: string[],
        public readonly preFilterJobTitles: string[],
        public readonly userFilterJobTitles: string[],
        public readonly limit: number,
        public readonly offset: number,
        public readonly invertTitleFilter: boolean
    ) {
    }
}

export class JobLocationsRequest {
    public constructor(
        public readonly jobTypes: string[],
        public readonly jobCompanies: string[],
        public readonly preFilterJobTitles: string[]
    ) {
    }
}

export class JobPreviewResponse {
    public constructor(public readonly jobs: Job[], public readonly total: number) {
    }
}

export type Job = {
    url: string;
    title: string;
    locations: string[];
    company: string;
}

export class JobLocationsResponse {
    public constructor(public readonly locations: string[]) {
    }
}

@autoRegister()
export class JobService {
    private jobsUrl: string;
    private locationsUrl: string;

    public constructor(
        private securedRequestExecutor: SecuredRequestExecutor = resolve(SecuredRequestExecutor),
        languages: LanguagesService = resolve(LanguagesService)
    ) {
        const language = languages.activeLanguageId();
        this.jobsUrl = `/enbw-eop/eop-app-job/rest/stable/${language}/jobs`;
        this.locationsUrl = `/enbw-eop/eop-app-job/rest/stable/${language}/locations`;
    }

    public async jobs(request: JobPreviewRequest): Promise<JobPreviewResponse> {
        const url = this.jobsUrl + "?" + new URLSearchParams([
            ["limit", request.limit.toString()],
            ["offset", request.offset.toString()],
            ...(request.jobTypes.map(jobType => ["jobTypes", jobType])),
            ...(request.jobLocations.map(jobLocation => ["jobLocations", jobLocation])),
            ...(request.jobCompanies.map(jobCompany => ["jobCompanies", jobCompany])),
            ...(request.preFilterJobTitles.map(jobTitle => ["preFilterJobTitles", jobTitle])),
            ...(request.userFilterJobTitles.map(jobTitle => ["userFilterJobTitles", jobTitle])),
            ["invertTitleFilter", request.invertTitleFilter.toString()]
        ]).toString();

        const getRequest = (): Promise<Response> => fetch(url);
        const response = await this.securedRequestExecutor.execute(getRequest);

        return promiseFromJsonResponse(response);
    }

    public async locations(request: JobLocationsRequest): Promise<JobLocationsResponse> {
        const url = this.locationsUrl + "?" + new URLSearchParams([
            ...(request.jobTypes.map(jobType => ["jobTypes", jobType])),
            ...(request.jobCompanies.map(jobCompany => ["jobCompanies", jobCompany])),
            ...(request.preFilterJobTitles.map(jobTitle => ["preFilterJobTitles", jobTitle]))
        ]).toString();

        const getRequest = (): Promise<Response> => fetch(url);
        const response = await this.securedRequestExecutor.execute(getRequest);

        return promiseFromJsonResponse(response);
    }
}